/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import diagnoResource from '../../../../share/util/diagnose';
import config from '../../../../share/config';
import shareVehicle from '../../../../share/util/vehicle';
import exam from '../../../../share/util/inputValidator';
import vinInfoUtils from "../../../../util/vinInfoUtils";
import customProgress from "../../../../share/components/vue/CustomProgress";
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { PHASE, OPACITY, TIMER } from './constants';
import { start } from './start';
import { execute } from './execute';
import { pollingStop } from './pollingStop';
import { communicationStop } from './communicationStop';
import { terminate } from './terminate';
import { save } from './save';
import { dispose } from './dispose';

export default {
  name: 'requirement146',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      isDisabledExecute: false,
      systemId: '',
      modal_requirement_146: false,
      requestId: '',
      sessionTransitionFlag: false,
      slipHold: '',
      startTime: null,
      opacityValue: OPACITY.DEFAULT,
      completeMessage: '',
      // プログレスバー
      progress: 0,
      timer: 0,
      waitingTime: 0,
      // プロジェクトデータ保存ダイアログ用
      vin: null,                                                                     // データ保存画面のVIN/車両番号
      vehicleName: null,                                                             // データ保存画面の車両
      displayProjectID: '',                                                          // データ保存画面のID
      hideRegistrationNumber: true,                                                  // データ保存画面の登録番号表示フラグ
      registrationNumber: null,                                                      // データ保存画面の登録番号表示
      isAlert: {},                                                                   // データ保存画面の入力制限オブジェクト
      diagnoseComment: '',                                                           // データ保存画面の診断コメント
      functionName: 'individual.individual_diagnosis_function_work_support',         // データ保存画面の機能名
      dataName: '',                                                                  // データ保存画面のデータ名
      memo: '',                                                                      // データ保存画面のメモ
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  components: {
    'custom-Progress': customProgress,
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
      vehicleInfo: state => state.vehicle.vehicleInfo,
      vehicleModelDisplayNameList: state => state.masterResources.vehicleModelDisplayNameList,
      language: state => state.systems.language,
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.ERROR:
        case PHASE.DEFAULT_SESSION_ERROR:
          return 'danger';
        case PHASE.COMPLETE:
          return 'success';
        case PHASE.START:
        case PHASE.CANCEL_CONFIRM:
        case PHASE.EXECUTE:
        case PHASE.COMMUNICATION_JUDGE:
        case PHASE.SAVE:
        default:
          return 'secondary';
      }
    },

    /** ダイアログのヘッダータイトル */
    headerTitle() {
      // 確認： 待機、キャンセル確認表示
      const ConfirmationTitle = 'message.common_title_confirmation';
      // 実行中： 計測中表示、変速比変化異常判定中
      const ExecutionTitle = 'individual.common_title_execution';
      // 成功： 変速比変化異常判定結果
      const SuccessTitle = 'message.common_title_success';
      // 入力： プロジェクトデータ保存
      const InputTitle = 'individual.label_input';
      // エラー： エラー、DefaultSessionエラー
      const ErrorTitle = 'message.common_title_error';
      const titleMap = {
        start: ConfirmationTitle,
        execute: ExecutionTitle,
        cancel_confirm: ConfirmationTitle,
        communication_judge: ExecutionTitle,
        complete: SuccessTitle,
        save: InputTitle,
        error: ErrorTitle,
        defaultSessionError: ErrorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    /** 表示メッセージ */
    workSupportMessage() {
      const messageMap = {
        start: `${this.i18nWsKey}.REQ_WS_146_TXT_1`,
        execute: `${this.i18nWsKey}.REQ_WS_146_TXT_2`,
        cancel_confirm: `${this.i18nWsKey}.REQ_WS_146_TXT_3`,
        communication_judge: `${this.i18nWsKey}.REQ_WS_146_TXT_4`,
        complete: this.completeMessage,
        error: `${this.i18nWsKey}.REQ_WS_146_TXT_6`,
        defaultSessionError: `${this.i18nWsKey}.REQ_WS_146_TXT_6`,
      };
      return this.phase === 'complete' ? messageMap[this.phase] : this.$t(messageMap[this.phase]);
    },

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断のうちどの機能なのか判別する
    operationScreen() {
      return diagnoResource.operationFrom(this.$router.currentRoute.value.name);
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      // プロジェクトデータ保存画面表示内容取得
      this.screenProjectData();
      this.modal_requirement_146 = true;
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_146 = false;
    },

    //----- バックエンドAPI実行メソッド -----------------------------------------------//
    /** 開始処理 */
    async startProcess() {
      // 開始処理API実行
      await start(this);
    },

    /** 周期通信開始・取得処理 */
    async executeProcess() {
      // 計測中表示へ遷移
      this.phase = PHASE.EXECUTE;

      // // 計測中プログレスバー動作
      // 30分間（1800秒）プログレスバーを動作し続ける。
      this.timer = setInterval(() => {
        this.progress += 1; // 1%ずつ進行
        if (this.progress >= 100) {
          this.progress = 0; // 100%に達したら0に戻す
        }
      }, TIMER.INTERVAL); // 0.04秒ごとに1%進行

      // 30分後にカウントストップ
      setTimeout(() => {
        clearInterval(this.timer);
      }, this.waitingTime + 60000); // 30分＋ポーリング分のマージン60秒

      // 周期通信開始・取得API実行
      await execute(this);

      // タイマー停止
      clearInterval(this.timer);
      this.timer = 0;
    },

    /** 周期通信停止（正常時）処理 */
    async communicationStopSuccessProcess() {
      // 変速比変化異常判定中へ遷移
      this.phase = PHASE.COMMUNICATION_JUDGE;
      // 周期通信停止API実行（正常時）
      await communicationStop(this, true);
    },

    /** 周期通信停止（異常時）処理 */
    async communicationStopErrorProcess() {
      // 周期通信停止API実行（異常時）
      await communicationStop(this, false);
    },

    /** ポーリング停止処理 */
    async pollingStopProcess() {
      // キャンセル確認表示のキャンセル、OKボタンを非活性にする
      this.isDisabledExecute = true;
      // タイマー停止
      clearInterval(this.timer);
      this.timer = 0;
      // ポーリング停止API実行
      await pollingStop(this);
    },

    /**
     * DefaultSession遷移処理
     * @param {boolean} isMoveCloseWizard 機能終了遷移フラグ
     */
    async defaultSessionProcess(isMoveCloseWizard) {
      // DefaultSession遷移API実行
      await terminate(this, isMoveCloseWizard);
    },

    /** エラー処理 */
    async errorProcess() {
      // セッション遷移有無フラグの判定
      if (this.sessionTransitionFlag) {
        // true の場合、DefaultSession遷移処理
        await this.defaultSessionProcess(true);
      } else {
        // false の場合、DisposeAPI実行
        await dispose(this);
      }
    },

    /** キャンセルボタン押下処理 */
    onCancelClicked() {
      switch (this.phase) {
        case PHASE.START:
          // 画面を閉じる
          this.closeWizard();
          break;
        case PHASE.CANCEL_CONFIRM:
          // 計測中表示へ遷移（画面遷移のみ ※周期通信開始・取得APIは再実行しない）
          this.phase = PHASE.EXECUTE;
          break;
        default:
          break;
      }
    },

    /** OKボタン押下処理 */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.START:
          // 開始処理
          await this.startProcess();
          break;
        case PHASE.CANCEL_CONFIRM:
          // ポーリング停止処理
          await this.pollingStopProcess();
          break;
        case PHASE.COMPLETE:
          // DefaultSession遷移処理
          await this.defaultSessionProcess(false);
          break;
        case PHASE.ERROR:
          // エラー処理
          await this.errorProcess();
          break;
        case PHASE.DEFAULT_SESSION_ERROR:
          // プロジェクトデータ保存画面へ遷移
          this.phase = PHASE.SAVE;
          break;
        default:
          break;
      }
    },

    /** 保存ボタン押下処理 */
    async onSaveClicked() {
      // 入力チェック
      if (this.validate()) {
        // プロジェクトデータ保存API実行
        await save(this);
      }
    },

    /** 終了ボタン押下 */
    onFinishClicked() {
      // キャンセル確認表示へ遷移
      this.phase = PHASE.CANCEL_CONFIRM;
    },

    //----- プロジェクトデータ保存画面用メソッド ------------------------------------------------------//
    /** 入力検証アラートのクリア */
    clearAlert: function () {
      this.isAlert = {
        dataName: { valid: null, msg: '' },
        diagnoseComment: { valid: null, msg: '' },
        memo: { valid: null, msg: '' },
      };
    },

    /** プロジェクトデータ保存画面表示内容取得 */
    screenProjectData: function () {
      // 入力チェックエラー解除
      this.clearAlert();

      // プロジェクト保存画面に必要な情報を設定
      const vehicle = this.vehicleInfo.vehicle;
      this.vin = vehicle.vin.vin;
      this.displayProjectID = vehicle.project.id;

      // 車種情報から表示名称を取得
      const spec = shareVehicle.createModelDisplaySpec(vehicle.vin.type, vehicle.spec);
      this.vehicleName = shareVehicle.createModelDisplayNameCurrentLanguage(this.vehicleInfo, this.vehicleModelDisplayNameList, this.language, '{0} {1}', spec);
      // 登録番号取得
      if (vehicle.vin.type !== config.VEHICLEINFO_TYPE_VIN) {
        if (vinInfoUtils.outputRegistrationNumber(vehicle.vin)) {
          this.registrationNumber = vinInfoUtils.createRegistrationNumber(vehicle.vin);
        }
        else {
          this.registrationNumber = "";
        }
      } else {
        // 海外VINの場合、登録番号は画面から非表示にする
        this.hideRegistrationNumber = false;
      }
    },

    /** プロジェクト保存画面の入力検証処理 */
    validate() {
      // 検証結果キャッシュの初期化
      this.clearAlert();
      const resultPool = [];

      // データ名の必須チェック
      this.isAlert.dataName = exam.excuteValidateCommons(this.dataName, true, 0, config.MEMOINFO_DETA_NAME_MAX_LEN);
      resultPool.push(this.isAlert.dataName.valid);

      // 診断コメントの文字数チェック
      this.isAlert.diagnoseComment = exam.excuteValidateCommons(this.diagnoseComment, false, 0, config.MEMOINFO_MAX_LEN);
      resultPool.push(this.isAlert.diagnoseComment.valid);

      // メモの文字数チェック
      this.isAlert.memo = exam.excuteValidateCommons(this.memo, false, 0, config.MEMOINFO_MAX_LEN);
      resultPool.push(this.isAlert.memo.valid);

      const faults = resultPool.filter(el => el === false);
      return faults.length === 0;
    },

    //----- 異常コールバックメソッド -----------------------------------------------//
    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    },

    /**
     * API 通信共通バリデーションエラー発生のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },
  },

  /**
   * DOM作成後実行処理
   */
  async mounted() {
    // 初期化
    this.init();
  },
};